import { fetchAuthSession, signOut } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface ApiError {
  error: string;
}

async function getAuthHeaders(forceRefresh = false): Promise<HeadersInit> {
  const session = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
  const token = session.tokens?.idToken?.toString();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * The session's token could not be made valid (expired/invalid even after a
 * forced refresh). Clear it and bounce to the login screen with a reason flag,
 * so the user gets a clean re-login prompt instead of a cascade of 401s.
 */
async function forceRelogin(): Promise<never> {
  try {
    await signOut();
  } catch {
    // ignore — we're redirecting to login regardless
  }
  if (typeof window !== 'undefined') {
    const url = new URL('/login', window.location.origin);
    url.searchParams.set('reason', 'expired');
    window.location.assign(url.toString());
  }
  throw new Error('Your session expired — please sign in again.');
}

/**
 * fetch with the bearer token attached. On a 401 (typically a stale token that
 * Amplify didn't refresh in time — e.g. a network blip during refresh), force a
 * token refresh and retry exactly once. If it's still unauthorized, the session
 * is genuinely dead, so sign out and send the user to re-login.
 */
async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  let response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: await getAuthHeaders(),
  });

  if (response.status === 401) {
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        ...init,
        headers: await getAuthHeaders(true), // force a fresh token
      });
    } catch {
      await forceRelogin();
    }
    if (response.status === 401) {
      await forceRelogin();
    }
  }

  return response;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorBody: ApiError = await response.json().catch(() => ({
      error: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(errorBody.error || 'API request failed');
  }
  return response.json();
}

export async function apiGet<T>(path: string): Promise<T> {
  return handleResponse<T>(await authedFetch(path, { method: 'GET' }));
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return handleResponse<T>(
    await authedFetch(path, { method: 'POST', body: JSON.stringify(body) }),
  );
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return handleResponse<T>(
    await authedFetch(path, { method: 'PATCH', body: JSON.stringify(body) }),
  );
}

export async function apiDelete(path: string): Promise<void> {
  const response = await authedFetch(path, { method: 'DELETE' });
  if (!response.ok) {
    const errorBody: ApiError = await response.json().catch(() => ({
      error: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(errorBody.error || 'API request failed');
  }
}
